import { Injectable, Injector } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SystemDeviceUserData, ProtegusUserData, CurrentUserData, SystemProtegusUserData, UserSystemData, Region } from 'src/api/v3/common';
import { AllUsersUserData, GetSingleUserResponse } from 'src/api/v3/user';
import { autoinject } from 'src/shim';
import { DataTableGetter } from '../company/components/data-table/data-table.component';
import { TDeviceUser } from '../models/device-user';
import { HomeConfiguration } from '../models/home-configuration';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { PlatformService } from './platform.service';
import { RequestService } from './request.service';
import { ReactionService } from './system/reaction.service';
import { FilterCriteria } from '../company/pages/company-users/company-users.component';
import { Subject } from 'rxjs';
import { CompanyService } from './company.service';
import { GlobalService } from '../services/global.service';
import { LocatorService } from '../services/locator.service';
import { IpcomService } from './ipcom.service';
import { IPCom } from '../models/ipcom';
import { PermissionService } from './permission.service';
import { TRegionListItemData } from '../models/region-list-item-data';
import { DropdownItem } from '../ui/dropdown/dropdown.component';

export const defaultHomeConfiguration: HomeConfiguration = {
  elementOrder: [
    { position: 1, type: 'events', visible: true },
    { position: 2, type: 'areas', visible: true },
    { position: 3, type: 'outputs', visible: true },
    { position: 4, type: 'sensors', visible: true },
    { position: 5, type: 'cameras', visible: true },
    { position: 6, type: 'thermostats', visible: true },
  ],
  systemId: 0,
  visibleAreas: [],
  visibleOutputs: [],
  visibleSensors: [],
  visibleCameras: [],
  visibleThermostats: [],
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public currentUser: CurrentUserData = null;
  public isCurrentUserFetched = false;
  public readonly users = new Map<number, ProtegusUserData>();
  public readonly systemUsers = new Map<number, UserSystemData[]>();
  public readonly systemDeviceUsers = new Map<number, Map<number, TDeviceUser>>();
  public usersForList: DropdownItem<number>[] = [];
  private previousFilterCriteria = null;
  private onUserDataChangedSource = new Subject<CurrentUserData>();
  public onUserDataChanged = this.onUserDataChangedSource.asObservable();
  public installerAssistance: { time: number; issue: string } = undefined;
  public companyInstallers = [];
  public simpleUsers = [];
  public countries = [];
  public superAdmins = [];
  public companies = [];
  public filteredUsers: any[] = [];
  public userPassword = '';
  public appVersionDetails = null;
  public eventPerPage = 30;
  public userRegions: Region[] = [];
  public userHasFewRegions = false;
  public refreshReactionList = new Subject<boolean>();
  public onRefreshReactionList = this.refreshReactionList.asObservable();
  public refreshIPComList = new Subject<boolean>();
  public onRefreshIPComList = this.refreshIPComList.asObservable();
  public allUsersLoaded = false;

  constructor(
    private auth: AuthService,
    private platform: PlatformService,
    private sanitizer: DomSanitizer,
    private req: RequestService,
    private l: LoggerService,
    private permissionService: PermissionService,
  ) {
    this.auth.onAccountOrRegionChnage.subscribe(() => {
      this.users.clear();
      this.usersForList = [];
    });
    this.onUserDataChanged.subscribe(this.auth.registerCurrentUserData);
    setTimeout(() => {
      if (this.auth.hasToken()) {
        this.auth.loadUserData().catch((e) => this.l.log('Napavyko gauti user duomenų', 'UserService', e.message));
      }
    }, 0);
  }

  private get reactionsService() { return autoinject(LocatorService.injector, ReactionService); }
  private get companyService() { return autoinject(LocatorService.injector, CompanyService); }
  private get g() { return autoinject(LocatorService.injector, GlobalService); }
  private get ipcomService() { return autoinject(LocatorService.injector, IpcomService); }

  public setCurrentUser(user?: CurrentUserData) {
    this.l.log('Gauti vartotojo duomenys:', 'UserService', user);
    this.currentUser = user ? JSON.parse(JSON.stringify(user)) : null;
    if ( !this.currentUser ) { return; }

    if (this.platform.isAndroid()) { this.platform.androidHandler().setDateTimeFormat(user.date_format); }
    if (this.platform.isApple()) { this.platform.appleHandler().setDateTimeFormat.postMessage(user.date_format); }
    if ( user.logo !== '' ) {
      this.currentUser.logoDataForStorage = user.logo as string;
      this.currentUser.logo = this.sanitizer.bypassSecurityTrustUrl(user.logo as string);
    }
    this.currentUser.homeConfigurations = this.processHomeConfigurations(user.homeConfigurations);
    if ( user.reactions ) { user.reactions.forEach((reaction) => this.reactionsService.ingestReaction(reaction)); }
    if ( user.ipcoms ) { user.ipcoms.forEach((i) => this.ipcomService.ingestIpcom(i)); }
    Object.defineProperty(this.currentUser, 'ipcoms', {
      get: () => [...this.ipcomService.ipcoms.values()],
      set: (ipcoms: IPCom[]) => {
        this.ipcomService.clear();
        ipcoms.forEach((i) => this.ipcomService.ingestIpcom(i));
      }
    });
    if ( user.regions ) {
      for (const iRegion of this.currentUser.regions) {
        const n = iRegion.api_host.indexOf('://');
        if (n !== -1) {
          iRegion.api_host_to_use = iRegion.api_host.substr(n + 3);
        } else {
          iRegion.api_host_to_use = iRegion.api_host;
        }
      }
    }
    this.currentUser.permission_rules.forEach((r) => this.permissionService.ingestIntoSessionStorage(r));
    if ( user.companies ) { user.companies.forEach((c) => this.companyService.ingestCompany(c)); }
    this.appVersionDetails = user.appVersion;
    this.g.checkAndNotifyAboutNewVersion();
  }

  public ingestDeviceUser(user?: SystemDeviceUserData): TDeviceUser | undefined {
    if (!user) { return; }
    const { keyboard_code, pin, enable_data, pgms, present, schedule_no, system_code_no, user_id, ...rest } = user;

    const processedUser: TDeviceUser = {
      code: keyboard_code ?? pin ?? '',
      zone_number: system_code_no !== undefined ? system_code_no : user_id !== undefined ? user_id : 0,
      enable_data: enable_data !== undefined ? enable_data : 0,
      schedule_no: schedule_no !== undefined ? schedule_no : 0,
      pgms: pgms !== undefined ? pgms : -1,
      present: present ?? false,
      isOwner: false,
      ownerPermissions: undefined,
      ...rest,
    };
    if (!this.systemDeviceUsers.has(user.system_id)) {
      this.systemDeviceUsers.set(user.system_id, new Map<number, TDeviceUser>());
    }
    if ( !this.systemDeviceUsers.get(user.system_id).has(user.id) ) {
      this.systemDeviceUsers.get(user.system_id).set(user.id, processedUser);
    }

    return processedUser;
  }

  public ingestDeviceUserData(user: TDeviceUser, systemId: number) {
    if (!this.systemDeviceUsers.has(systemId)) {
      this.systemDeviceUsers.set(systemId, new Map<number, TDeviceUser>());
    }
    if ( !this.systemDeviceUsers.get(systemId).has(user.id) ) {
      this.systemDeviceUsers.get(systemId).set(user.id, user);
    }
  }

  public ingestProtegusUser(user: SystemProtegusUserData, system: { id: number; name: string }): ProtegusUserData | undefined {
    if (!user || !system.id) { return; }

    const { master, personal_permissions, system_permissions_id, ...rest } = user;
    const userSystemLite = { id: system.id, name: system.name };

    if (!this.users.has(user.id)) {
      rest.systems = [];
      rest.systems.push(userSystemLite);
      this.users.set(user.id, rest);
    } else {
      const existingUser = this.users.get(user.id);
      const userSystemFound = existingUser.systems.find(sys => sys.id === userSystemLite.id);
      if(!userSystemFound) {
        existingUser.systems.push(userSystemLite);
        this.users.set(user.id, existingUser);
      }
    }
    let systemData = this.systemUsers.get(system.id);
    if ( !systemData ) {
      systemData = this.systemUsers.set(system.id, []).get(system.id);
    }
    const userSystemData = systemData.find(u => u.id === user.id );
    if ( !userSystemData ) {
      console.log(`Ingest protegus user [${system.id}]: ${user.email}`);
      systemData.push({master, system_permissions_id, personal_permissions, id: rest.id});
    }
    return rest;
  }

  public ingestUserLite(user: AllUsersUserData): ProtegusUserData {
    if (!user) { return; }
    const processedUser: ProtegusUserData = {
      company: '',
      company_id: 0,
      created_at: '',
      dashboard_order: '',
      phone: '',
      phone_number: '',
      privacy_policy_accepted: true,
      soc_account: false,
      soc_type: '',
      social_id: '',
      ...user,
    };
    console.log('Ingest lite user: ', user);
    if (!this.users.has(user.id)) {
      this.users.set(user.id, processedUser);
    } else if(user.systems && user.systems.length > 0) {
      const existingUser = this.users.get(user.id);
      user.systems.forEach(system => {
        const userSystemLite = { id: system.id, name: system.name };
        const userSystemFound = existingUser.systems.find(sys => sys.id === userSystemLite.id);
        if(!userSystemFound) {
          existingUser.systems.push(userSystemLite);
          this.users.set(user.id, existingUser);
        }
      });
    }
    return processedUser;
  }

  private processHomeConfigurations(configurations?: any[]): HomeConfiguration[] {
    if (!configurations || !configurations.length) {
      return [defaultHomeConfiguration];
    }
    return configurations.map(({ element_order, system_id, areas, outputs, sensors, cameras, thermostats }) => {
      const elementOrder = JSON.parse(element_order ?? 'null') ?? defaultHomeConfiguration.elementOrder;
      if (!elementOrder.find((e) => e.type === 'cameras')) { elementOrder.push(defaultHomeConfiguration.elementOrder[4]); }
      if (!elementOrder.find((e) => e.type === 'thermostats')) { elementOrder.push(defaultHomeConfiguration.elementOrder[5]); }
      return {
        systemId: system_id,
        elementOrder,
        visibleAreas: JSON.parse(areas ?? 'null') ?? defaultHomeConfiguration.visibleAreas,
        visibleOutputs: JSON.parse(outputs ?? 'null') ?? defaultHomeConfiguration.visibleOutputs,
        visibleSensors: JSON.parse(sensors ?? 'null') ?? defaultHomeConfiguration.visibleSensors,
        visibleCameras: JSON.parse(cameras ?? 'null') ?? defaultHomeConfiguration.visibleCameras,
        visibleThermostats: JSON.parse(thermostats ?? 'null') ?? defaultHomeConfiguration.visibleThermostats,
      };
    });
  }

  public async loadAllUsers(): Promise<void> {
    const result = await this.req.user.getAllUsers({ all: true, loadedUsersCount: this.allUsersLoaded ? this.users.size: 0 }).toPromise();
    if (result.success) {
      result.users.forEach((user) => this.ingestUserLite(user));
      result.rules.forEach((rule) => this.permissionService.ingestIntoSessionStorage(rule));
    }
    this.allUsersLoaded = true;
  }

  public async loadSingleUserData(id: number): Promise<GetSingleUserResponse> {
    const result = await this.req.user.getSingleUser({ id }).toPromise();
    return result;
  }

  public getUsersGetter(resultsFiltered: boolean): DataTableGetter<ProtegusUserData> {
    if(resultsFiltered) { this.users.clear(); }
    return async (current, columns, more) => {
      if(!more && current <= this.users.size && !resultsFiltered) {
        return [...this.users.values()];
      }
      this.previousFilterCriteria = null;
      await this.loadAllUsers().then(() => {
        resultsFiltered = false;
      });
      return [...this.users.values()];
    };
  }

  public async loadFilteredUsers(filterCriteria: FilterCriteria): Promise<any> {
    const result = await this.req.user.filterUsers({
      searchPhrase: filterCriteria.searchPhrase,
      searchFields: filterCriteria.searchFields,
      role: -1,
      sort: 0,
      paginationPage: filterCriteria.currentPage === filterCriteria.lastPage ? filterCriteria.lastPage : filterCriteria.currentPage + 1
    }).toPromise();
    if (result.success) {
      result.list.data.forEach((dataItem: AllUsersUserData) => this.ingestUserLite(dataItem));
      return result.list;
    }
  }

  public getFilteredUsersGetter(filterCriteria: FilterCriteria): DataTableGetter<ProtegusUserData> {
    return async (current, columns, more) => {
      const filterCriteriaString = JSON.stringify(filterCriteria);
      const newFilterCriteria = this.previousFilterCriteria !== filterCriteriaString;

      if(filterCriteria.lastPage === 0 && newFilterCriteria) {
        this.users.clear();
      }
      if(!more && current <= this.users.size && this.users.size > 0) {
        return [...this.users.values()];
      }
      if(newFilterCriteria) {
        this.previousFilterCriteria = filterCriteriaString;
        await this.loadFilteredUsers(filterCriteria).then((result) => {
          filterCriteria.currentPage = result.current_page;
          filterCriteria.lastPage = result.last_page;
        });
      }
      return [...this.users.values()];
    };
  }

  /** Iššaukia onUserDataChanged event */
  public change() {
    this.onUserDataChangedSource.next(this.currentUser);
  }

  public saveCurrentUser() {
    const userString = JSON.stringify(this.currentUser);
    this.l.log('Saugomi vartotojo duomenys:', 'UserService', this.currentUser);
    localStorage.setItem('user_data', userString);
  }

  public setLoginType(type: string) {
    localStorage.setItem('login_type', type);
  }

  public getLoginType(): string {
    return localStorage.getItem('login_type');
  }

  public clearUserData() {
    localStorage.removeItem('token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('last_system');
    localStorage.removeItem('last_system_data');
    localStorage.removeItem('login_type');
    this.setCurrentUser(null);
    this.change();
    this.saveCurrentUser();
  }

  public loadUserDataFromStorage() {
    const userString = localStorage.getItem('user_data');
    if ( !userString ) { return; }

    const temp = JSON.parse(userString);
    if ( !temp ) {
      this.l.log('Užkraunami išsaugoti vartotojo duomenys: NULL', 'UserService');
      this.setCurrentUser(null);
      this.change();
      return;
    }

    if (temp.hasOwnProperty('Id')) {
      temp.id = temp.Id;
      delete temp.Id;
    }
    if (temp.hasOwnProperty('Email')) {
      temp.email = temp.Email;
      delete temp.Email;
    }
    if (temp.hasOwnProperty('Name')) {
      temp.name = temp.Name;
      delete temp.Name;
    }
    if (temp.hasOwnProperty('Role')) {
      temp.role = temp.Role;
      delete temp.Role;
    }
    if (temp.hasOwnProperty('Phone')) {
      temp.phone = temp.Phone;
      delete temp.Phone;
    }
    const hasPermissions = temp.hasOwnProperty('permissions');
    if ( !hasPermissions || !temp.permissions ) {
      temp.permissions = PermissionService.getCopyOfRule(PermissionService.defaultGeneralUserRule);
    }
    if (temp.hasOwnProperty('ipcoms') && !temp.permissions.permissions.ipcom_settings.view) {
      delete temp.ipcoms;
    }
    if (temp.hasOwnProperty('settings') && !temp.permissions.permissions.global_settings.view) {
      delete temp.settings;
    }
    this.currentUser = temp;
    if (this.currentUser.logoDataForStorage !== undefined && this.currentUser.logoDataForStorage !== '') {
      this.currentUser.logo = this.sanitizer.bypassSecurityTrustUrl(this.currentUser.logoDataForStorage);
    }
    this.l.log('Užkraunami išsaugoti vartotojo duomenys: ', 'UserService', this.currentUser);
    this.change();
  }

  public getRegionsGetter(): DataTableGetter<TRegionListItemData> {
    return async (current, columns, more) => this.currentUser.regions;
  };

  public populateUserRegions(): void  {
    if(!this.currentUser) {
      return;
    }
    if(this.currentUser.permissions.permissions.regions.view) {
      this.userRegions = this.currentUser.regions;
    } else {
      this.checkIfUserHasFewRegions();
      if (this.userHasFewRegions) {
        this.auth.loadMergedRegionAccounts();
        const accounts = this.auth.mergedRegionAccounts;
        const user = accounts.find(acc => acc.id === this.currentUser.id);
        this.userRegions = (user && user.regions.length > 1) ? user.regions : [];
      } else {
        this.userRegions = [];
      }
    }
  }

  private checkIfUserHasFewRegions(): void {
    this.auth.loadMergedRegionAccounts();
    const accounts = this.auth.mergedRegionAccounts;
    const user = accounts.find(acc => acc.id === this.currentUser.id);
    this.userHasFewRegions = user && user.regions.length > 1;
  }

  public refreshReactionListChange(value: boolean) {
    this.refreshReactionList.next(value);
  }

  public refreshIPComListChange(value: boolean) {
    this.refreshIPComList.next(value);
  }

  public canViewAnyOfGeneralPermissions(): boolean {
    for ( const iPermission of this.permissionService.allGeneralPermissions ) {
      if ( this.currentUser.permissions.permissions[iPermission].view ) { return true; }
    }

    return false;
  }
}
